import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { consumePendingStartParam } from '@/lib/startParam';
import { routeForStartParam, INVITE_ROUTE_PREFIX } from '@/routes/startParamRoutes';

/**
 * L'altra meta' dello stash: appena l'app e' utilizzabile, il payload messo da
 * parte al bootstrap diventa una navigazione.
 *
 * ═══ 🔴 SI CONSUMA UNA VOLTA SOLA, E SI CANCELLA ANCHE SE L'ATTERRAGGIO
 *     FALLISCE — e' la riga piu' importante di questo file ═══
 * Si chiama `consumePendingStartParam` (che LEGGE E CANCELLA), mai
 * `peekPendingStartParam`. La cancellazione avviene QUI, prima della
 * navigazione, quindi vale per ogni esito: invito scaduto, gia' preso, ruolo
 * mancante, server muto, utente che chiude la schermata.
 * ⛔ Non spostare la cancellazione dentro la schermata di atterraggio «quando
 * l'accettazione riesce»: un payload che sopravvive a un atterraggio fallito
 * dirotta un'APERTURA SUCCESSIVA E INNOCENTE della app — l'utente apre la TMA
 * per i fatti suoi e si ritrova su un invito di mezz'ora fa.
 * 📌 E' l'unico modo in cui questo meccanismo puo' fare DANNO invece che
 * semplicemente non funzionare: non racconta niente e AGISCE.
 *
 * ⚠️ `ready` NON e' «autenticato». E' «autenticato E oltre l'onboarding»: un
 * ospite nuovo passa dal wizard, e navigare mentre quello e' a schermo
 * porterebbe via l'invito nel momento in cui l'utente non puo' agirci. Il
 * payload aspetta — ed e' per questo che lo stash dura piu' dell'invito
 * (v. `lib/startParam.ts`).
 */
export function usePendingInvite(ready: boolean): void {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!ready) return;
    // Gia' sulla schermata dell'invito (l'utente ci e' arrivato per suo conto, o
    // ha ricaricato): consumare qui significherebbe rimbalzarlo su se stesso.
    if (location.pathname.startsWith(INVITE_ROUTE_PREFIX)) return;

    // LEGGE E CANCELLA in un colpo: da qui in poi lo stash e' vuoto qualunque
    // cosa succeda a valle. Vedi il blocco in testa al file.
    const pending = consumePendingStartParam();
    if (!pending) return;

    const to = routeForStartParam(pending);
    if (to) navigate(to, { replace: true });
    // `location.pathname` volutamente FUORI dalle dipendenze: questo effetto
    // deve girare quando l'app diventa PRONTA, non a ogni cambio di rotta —
    // altrimenti un payload arrivato mentre si naviga verrebbe consumato in un
    // punto qualsiasi del percorso dell'utente.
    // ⚠️ Nessun `eslint-disable` qui: ce l'avevo messo per riflesso e il lint
    // l'ha rifiutato come DIRETTIVA INUTILE — la regola non si lamenta di questo
    // caso. Una soppressione che non sopprime niente e' peggio di nessuna:
    // sembra dire «lo so, e' voluto» su un avviso che non esiste, e la prossima
    // persona la legge come una deroga vera.
  }, [ready, navigate]);
}
